import WarningIcon from "@mui/icons-material/Warning";
import {
  Alert,
  Box,
  Paper,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import React, { Suspense } from "react";
import { useMatchEvents } from "../hooks/match_events.hooks";
import { useDirectusMatch } from "../hooks/matches.hooks";
import { LoadingGuard } from "./LoadingGuard";
import { MatchEventsTable } from "./MatchEventsTable";
import { MatchInfoDataList } from "./MatchInfoDataList";

const MatchEnigmaStatsTable = React.lazy(() =>
  import("./MatchEnigmaStatsTable").then((mod) => ({
    default: mod.MatchEnigmasStatsTable,
  }))
);

export function MatchEnigmaDetailViewTab({
  value,
  title,
  children,
  index,
  elevation = 3,
  padding = 2,
}: {
  value: number;
  index: number;
  title: string;
  children: React.ReactNode;
  elevation?: number;
  padding?: number;
}) {
  return (
    <Paper hidden={value !== index} elevation={elevation} sx={{ p: padding }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      {value === index && children}
    </Paper>
  );
}

export function MatchDetailView({ matchId }: { matchId: string }) {
  const [activeTab, setActiveTab] = React.useState(0);
  const [fetchEvents, setFetchEvents] = React.useState(activeTab === 0);
  const [fetchMatch, setFetchMatch] = React.useState(activeTab > 0);
  const [hasInconsistencies, setHasInconsistencies] = React.useState(false);
  const match = useDirectusMatch(matchId, { enabled: fetchMatch });
  const events = useMatchEvents({
    matchId: matchId,
    options: { enabled: fetchEvents },
  });
  const theme = useTheme();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    if (newValue === 0) {
      setFetchEvents(true);
    } else {
      setFetchMatch(true);
    }
  };

  const checkInconsistencies = () => {};

  return (
    <Box>
      {hasInconsistencies && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <WarningIcon sx={{ mr: 1 }} />
          Inconsistenze rilevate tra dati aggregati e dettagliati
        </Alert>
      )}

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Eventi" />
        <Tab label="Informazioni partita" />
        <Tab label="Statistiche Enigmi" />
      </Tabs>

      <MatchEnigmaDetailViewTab
        title={`Eventi (${events.data?.length ?? 0})`}
        index={0}
        value={activeTab}
      >
        <LoadingGuard isLoading={events.isLoading}>
          {events.data && <MatchEventsTable events={events.data} />}
        </LoadingGuard>
      </MatchEnigmaDetailViewTab>
      <MatchEnigmaDetailViewTab
        title="Dettagli match"
        index={1}
        value={activeTab}
      >
        <LoadingGuard isLoading={match.isLoading}>
          {match.data && <MatchInfoDataList match={match.data} />}
        </LoadingGuard>
      </MatchEnigmaDetailViewTab>
      <MatchEnigmaDetailViewTab title="Statistiche" index={2} value={activeTab}>
        <LoadingGuard isLoading={match.isLoading}>
          {match.data && (
            <Suspense fallback={<div>Loading stats...</div>}>
              <MatchEnigmaStatsTable stats={match.data.steps_challenge_stats} />
            </Suspense>
          )}
        </LoadingGuard>
      </MatchEnigmaDetailViewTab>
    </Box>
  );
}
